import { divideArrayIntoChunks } from '@pkg/basic'
import { and, eq, inArray, ne, not } from 'drizzle-orm'
import { sqliteMaxHostParameterCount } from '../constants'
import {
  accountEvmTxIndexRelationTable,
  accountPolkadotExtrinsicRelationTable,
  accountSolanaSignatureTable,
  accountTable,
  actionAccountRelationTable,
  actionSourceTable,
  actionTable,
  portfolioTable,
} from '../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../types'

/**
 * Selector for targeting accounts for deletion.
 * Can target either by group name or by specific account IDs.
 */
type AccountSelector =
  | {
      /** Target accounts by group name */
      type: 'group'
      /** The group name to target */
      group: string
    }
  | {
      /** Target specific account IDs */
      type: 'ids'
      /** Array of account IDs to target */
      accountIds: number[]
    }

/**
 * Context object containing all data needed for account deletion operations.
 * Includes related actions, sources, and blockchain transaction references.
 */
type DeletionContext = {
  /** All action IDs related to the accounts being deleted */
  actionIds: string[]
  /** Action IDs that can be safely deleted (not referenced by other accounts) */
  deleteActionIds: string[]
  /** Source IDs that only contain actions being deleted */
  deletingSources: string[]
  /** EVM transaction hashes related to the accounts */
  evmTxHashes: Uint8Array[]
  /** Polkadot block numbers related to the accounts */
  polkadotBlockNumbers: number[]
  /** Solana signatures related to the accounts */
  solanaSignatures: string[]
}

/**
 * Retrieves all data related to the accounts selected for deletion.
 * This includes actions, blockchain transaction references, and determines
 * which actions and sources can be safely deleted.
 *
 * @param database - Database instance for querying
 * @param selector - Account selector (by group or IDs)
 * @returns Promise resolving to deletion context with all related data
 */
const getRelatedData = async (
  database: DatabaseWithTransaction['database'],
  selector: AccountSelector,
): Promise<DeletionContext> => {
  const accountFilter =
    selector.type === 'group'
      ? eq(accountTable.group, selector.group)
      : inArray(accountTable.id, selector.accountIds)

  // For group selector, use simple queries
  if (selector.type === 'group') {
    const [
      actionRows,
      evmTxHashRows,
      polkadotBlockNumberRows,
      solanaSignatureRows,
    ] = await Promise.all([
      database
        .select({
          id: actionTable.id,
          timestamp: actionTable.timestamp,
        })
        .from(actionTable)
        .innerJoin(
          actionAccountRelationTable,
          eq(actionTable.id, actionAccountRelationTable.actionId),
        )
        .innerJoin(
          accountTable,
          eq(actionAccountRelationTable.accountId, accountTable.id),
        )
        .where(accountFilter),
      database
        .selectDistinct({
          hash: accountEvmTxIndexRelationTable.hash,
        })
        .from(accountEvmTxIndexRelationTable)
        .innerJoin(
          accountTable,
          eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
        )
        .where(accountFilter),
      database
        .selectDistinct({
          blockNumber: accountPolkadotExtrinsicRelationTable.blockNumber,
        })
        .from(accountPolkadotExtrinsicRelationTable)
        .innerJoin(
          accountTable,
          eq(accountPolkadotExtrinsicRelationTable.accountId, accountTable.id),
        )
        .where(accountFilter),
      database
        .selectDistinct({ signature: accountSolanaSignatureTable.signature })
        .from(accountSolanaSignatureTable)
        .innerJoin(
          accountTable,
          eq(accountSolanaSignatureTable.accountId, accountTable.id),
        )
        .where(accountFilter),
    ])

    const actionIds = [...new Set(actionRows.map((row) => row.id))]
    const { deleteActionIds, deletingSources } = await processActions(
      database,
      actionIds,
      selector,
    )

    return {
      actionIds,
      deleteActionIds,
      deletingSources,
      evmTxHashes: evmTxHashRows.map((row) => row.hash),
      polkadotBlockNumbers: polkadotBlockNumberRows.map(
        (row) => row.blockNumber,
      ),
      solanaSignatures: solanaSignatureRows.map((row) => row.signature),
    }
  }

  // For IDs selector, use chunked queries
  const [
    actionRows,
    evmTxHashRows,
    polkadotBlockNumberRows,
    solanaSignatureRows,
  ] = await Promise.all([
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .select({
            id: actionTable.id,
            timestamp: actionTable.timestamp,
          })
          .from(actionTable)
          .innerJoin(
            actionAccountRelationTable,
            eq(actionTable.id, actionAccountRelationTable.actionId),
          )
          .innerJoin(
            accountTable,
            eq(actionAccountRelationTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            hash: accountEvmTxIndexRelationTable.hash,
          })
          .from(accountEvmTxIndexRelationTable)
          .innerJoin(
            accountTable,
            eq(accountEvmTxIndexRelationTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            blockNumber: accountPolkadotExtrinsicRelationTable.blockNumber,
          })
          .from(accountPolkadotExtrinsicRelationTable)
          .innerJoin(
            accountTable,
            eq(
              accountPolkadotExtrinsicRelationTable.accountId,
              accountTable.id,
            ),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
    Promise.all(
      divideArrayIntoChunks(
        selector.accountIds,
        sqliteMaxHostParameterCount - 1,
      ).map((chunk) =>
        database
          .selectDistinct({
            signature: accountSolanaSignatureTable.signature,
          })
          .from(accountSolanaSignatureTable)
          .innerJoin(
            accountTable,
            eq(accountSolanaSignatureTable.accountId, accountTable.id),
          )
          .where(inArray(accountTable.id, [...chunk])),
      ),
    ).then((chunks) => chunks.flat()),
  ])

  const actionIds = [...new Set(actionRows.map((row) => row.id))]
  const { deleteActionIds, deletingSources } = await processActions(
    database,
    actionIds,
    selector,
  )

  return {
    actionIds,
    deleteActionIds,
    deletingSources,
    evmTxHashes: evmTxHashRows.map((row) => row.hash),
    polkadotBlockNumbers: polkadotBlockNumberRows.map((row) => row.blockNumber),
    solanaSignatures: solanaSignatureRows.map((row) => row.signature),
  }
}

const processActions = async (
  database: DatabaseWithTransaction['database'],
  actionIds: string[],
  selector: AccountSelector,
): Promise<{ deleteActionIds: string[]; deletingSources: string[] }> => {
  if (actionIds.length === 0) {
    return { deleteActionIds: [], deletingSources: [] }
  }

  // Find actions that are linked to other accounts not being deleted
  const actionAccountRelationRowsChunks = await Promise.all(
    divideArrayIntoChunks(actionIds, sqliteMaxHostParameterCount - 1).map(
      (chunk) => {
        const filter =
          selector.type === 'group'
            ? and(
                inArray(actionAccountRelationTable.actionId, [...chunk]),
                ne(accountTable.group, selector.group),
              )
            : and(
                inArray(actionAccountRelationTable.actionId, [...chunk]),
                not(
                  inArray(
                    actionAccountRelationTable.accountId,
                    selector.accountIds,
                  ),
                ),
              )

        return database
          .select({
            actionId: actionAccountRelationTable.actionId,
          })
          .from(actionAccountRelationTable)
          .innerJoin(
            accountTable,
            eq(actionAccountRelationTable.accountId, accountTable.id),
          )
          .where(filter)
      },
    ),
  )

  const actionAccountRelationRows = actionAccountRelationRowsChunks.flat()
  const existOtherAccountActionIdSet = new Set(
    actionAccountRelationRows.map((row) => row.actionId),
  )

  // Determine which actions can be deleted
  const deleteActionIds = actionIds.filter(
    (id) => !existOtherAccountActionIdSet.has(id),
  )

  // Find sources that only have actions being deleted
  const actionSourceRowsChunks = await Promise.all(
    divideArrayIntoChunks(deleteActionIds, sqliteMaxHostParameterCount).map(
      (chunk) =>
        database
          .select({
            source: actionSourceTable.id,
            actionId: actionTable.id,
          })
          .from(actionSourceTable)
          .innerJoin(
            actionTable,
            eq(actionSourceTable.id, actionTable.sourceId),
          )
          .where(inArray(actionTable.id, [...chunk])),
    ),
  )

  const actionSourceRows = actionSourceRowsChunks.flat()
  const sourceActionsMap = new Map<string, string[]>()
  for (const row of actionSourceRows) {
    const actionIds = sourceActionsMap.get(row.source) ?? []
    sourceActionsMap.set(row.source, [...actionIds, row.actionId])
  }

  const deletingSources = Array.from(sourceActionsMap.entries())
    .filter(([_, actionIds]) =>
      actionIds.every((actionId) => deleteActionIds.includes(actionId)),
    )
    .map(([source]) => source)

  return { deleteActionIds, deletingSources }
}

const createDeletionQueries = (
  context: DeletionContext,
  selector: AccountSelector,
) => {
  const {
    deleteActionIds,
    deletingSources,
    evmTxHashes,
    polkadotBlockNumbers,
    solanaSignatures,
  } = context

  const accountFilter =
    selector.type === 'group'
      ? eq(accountTable.group, selector.group)
      : inArray(accountTable.id, selector.accountIds)

  return [
    (tx: TransactionDatabase) => [
      // Update portfolio isTxGenerated flag if actions are being deleted
      ...(deleteActionIds.length > 0
        ? [tx.update(portfolioTable).set({ isTxGenerated: false })]
        : []),

      // Update analyzed flags for related transactions
      ...divideArrayIntoChunks(evmTxHashes, sqliteMaxHostParameterCount).map(
        (chunk) =>
          tx
            .update(accountEvmTxIndexRelationTable)
            .set({ analyzed: false })
            .where(inArray(accountEvmTxIndexRelationTable.hash, [...chunk])),
      ),
      ...divideArrayIntoChunks(
        polkadotBlockNumbers,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .update(accountPolkadotExtrinsicRelationTable)
          .set({ analyzed: false })
          .where(
            inArray(accountPolkadotExtrinsicRelationTable.blockNumber, [
              ...chunk,
            ]),
          ),
      ),
      ...divideArrayIntoChunks(
        solanaSignatures,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .update(accountSolanaSignatureTable)
          .set({ analyzed: false })
          .where(inArray(accountSolanaSignatureTable.signature, [...chunk])),
      ),
    ],
    (tx: TransactionDatabase) => [
      // Delete action sources
      ...divideArrayIntoChunks(
        deletingSources,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx
          .delete(actionSourceTable)
          .where(inArray(actionSourceTable.id, [...chunk])),
      ),
      // Delete accounts
      ...(selector.type === 'group'
        ? [tx.delete(accountTable).where(accountFilter)]
        : divideArrayIntoChunks(
            selector.accountIds,
            sqliteMaxHostParameterCount,
          ).map((chunk) =>
            tx.delete(accountTable).where(inArray(accountTable.id, [...chunk])),
          )),
      // Delete actions
      ...divideArrayIntoChunks(
        deleteActionIds,
        sqliteMaxHostParameterCount,
      ).map((chunk) =>
        tx.delete(actionTable).where(inArray(actionTable.id, [...chunk])),
      ),
    ],
    (tx: TransactionDatabase) => [
      // Update remaining actions generatedTx flag
      ...(deleteActionIds.length > 0
        ? [tx.update(actionTable).set({ generatedTx: false })]
        : []),
    ],
  ]
}

/**
 * Creates a factory for account deletion operations.
 * The factory provides methods to delete accounts either by group name
 * or by specific account IDs, handling all related data cleanup.
 *
 * @example
 * ```typescript
 * const factory = createAccountDeletionFactory()
 * await factory.deleteByGroup(database, 'test-group')
 * await factory.deleteByIds(database, [1, 2, 3])
 * ```
 *
 * @returns Factory object with deletion methods
 */
export const createAccountDeletionFactory = () => {
  return {
    async deleteByGroup(database: DatabaseWithTransaction, group: string) {
      const selector: AccountSelector = { type: 'group', group }
      const context = await getRelatedData(database.database, selector)
      const queries = createDeletionQueries(context, selector)
      await database.transaction(queries)
    },

    async deleteByIds(database: DatabaseWithTransaction, accountIds: number[]) {
      if (accountIds.length === 0) {
        return
      }

      const selector: AccountSelector = { type: 'ids', accountIds }
      const context = await getRelatedData(database.database, selector)
      const queries = createDeletionQueries(context, selector)
      await database.transaction(queries)
    },
  }
}
